import PropTypes from 'prop-types';
import { useState, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  Typography,
  Pagination,
  IconButton,
  Collapse,
  Tooltip,
  styled,
  TableContainer,
  TableBody,
  TableRow,
  TableCell,
  Table,
  Accordion,
  AccordionDetails,
  Stack,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Edit as EditIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import { useTranslation } from 'react-i18next';
import Error from '../Error';
import Spinner from '../Spinner';
import { useGetChildrensLotQuery } from '../../features/api/rest/apiLotsSlice';
import AddLotDialog from './AddLotDialog';
import DeleteLotDialog from './DeleteLotDialog';
import LotPlastic from './LotPlastic';

function TreeLotsCard({ ean, opk, id, authority, code, info = {}, from = null, to = null, searchParamsName = '' }) {
  const { type = '', surfaceWeight = null, pieceSurface = null, plasticPercentage = null, recycledPlasticPercentage = null, recyclingCode = null, recycling = null, materials = [] } = info || {};

  const formatDate = (dateToFormat) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateToFormat).toLocaleDateString('it-IT', options);
  };

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const modifiedSearchParamsName = searchParamsName ? `${searchParamsName}_` : '';
  const childrensLotForPage = parseInt(searchParams.get(`${modifiedSearchParamsName}psize`), 10) || 10;
  const childrensLotPage = parseInt(searchParams.get(`${modifiedSearchParamsName}page${id}`), 10) || 1;
  const ordering = searchParams.get(`${modifiedSearchParamsName}ordering`) || '-id';
  const [expanded, setExpanded] = useState(false);
  const expandMoreRef = useRef(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { childrensLot, childrensLotCount, isLoading, isFetching, isError } = useGetChildrensLotQuery(
    { ean, opk, id, ordering, page: childrensLotPage, psize: childrensLotForPage },
    {
      selectFromResult: (result) => ({
        ...result,
        childrensLot: result.data?.results,
        childrensLotCount: result.data?.count || 0,
      }),
    }
  );

  const handleScrollToExpandMore = () => {
    if (expandMoreRef.current) {
      expandMoreRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handlePageChange = (page) => {
    searchParams.set(`${modifiedSearchParamsName}page${id}`, page);
    navigate(`${pathname}?${searchParams.toString()}`);
  };

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));

  const StyledTableCell = styled(TableCell)(({ align }) => ({
    fontSize: 15,
    paddingLeft: 0,
    textAlign: align || 'right',
  }));

  const AccordionSummary = styled((props) => <MuiAccordionSummary expandIcon={<ExpandMoreIcon />} {...props} />)(({ theme }) => ({
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(-180deg)',
    },
    '& .MuiAccordionSummary-content': {
      marginLeft: theme.spacing(1),
    },
  }));

  const pagination = <Pagination sx={{ my: 2 }} count={Math.ceil(childrensLotCount / childrensLotForPage)} page={childrensLotPage} onChange={(event, newPage) => handlePageChange(newPage)} />;

  if (isLoading || isFetching) return <Spinner />;

  if (isError) return <Error />;
  return (
    <Box>
      <Card ref={expandMoreRef} sx={{ border: '1px solid #d3d4d5', my: 4 }} data-testid="ReviewCard">
        <CardContent>
          <Typography variant="h6">
            {authority} - {code}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            EAN: {ean}
          </Typography>
          <Accordion defaultExpanded disableGutters>
            <AccordionSummary>{t('products.lots.info')}</AccordionSummary>
            <AccordionDetails sx={{ py: 0 }}>
              <List disablePadding>
                {type && (
                  <ListItem sx={{ py: 0 }} divider>
                    <ListItemText primary={type} secondary={t('products.lots.type')} />
                  </ListItem>
                )}
                {type && recycledPlasticPercentage >= 0 && (
                  <ListItem sx={{ py: 0 }} divider>
                    <ListItemText primary={`${parseFloat((recycledPlasticPercentage * 100).toFixed(13))}%`} secondary={t('products.lots.recycledPlasticPercentage')} />
                  </ListItem>
                )}
                {recycling && (
                  <ListItem sx={{ py: 0 }} divider>
                    <ListItemText primary={`${recycling} ${recyclingCode ? `(Codice: ${recyclingCode})` : ''}`} secondary={t('products.lots.recycling')} />
                  </ListItem>
                )}
                <ListItem>
                  <ListItemText sx={{ py: 0 }} primary={`${from ? formatDate(from) : ''} - ${to ? formatDate(to) : ''}`} secondary={t('organizations.product.lots.validity')} />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>
          {type && type === 'packaging' && (
            <>
              {materials && materials.length > 0 && (
                <Accordion disableGutters>
                  <AccordionSummary>{t('products.lots.rawMaterials')}</AccordionSummary>
                  <AccordionDetails>
                    <TableContainer sx={{ width: 'fit-content', maxWidth: '100%' }}>
                      <Table size="small">
                        <TableBody>
                          {materials.map((material) => (
                            <StyledTableRow>
                              <StyledTableCell component="th" scope="row" align="left">
                                {material?.description ? material.description : '-'}
                              </StyledTableCell>
                              <StyledTableCell align="left">{material?.type ? material.type : '-'}</StyledTableCell>
                            </StyledTableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}
              <Accordion disableGutters>
                <AccordionSummary data-testid="PlasticExpandIcon">{t('organizations.product.lots.plastic.tax')}</AccordionSummary>
                <AccordionDetails>
                  <LotPlastic pieceSurface={pieceSurface} surfaceWeight={surfaceWeight} plasticPercentage={plasticPercentage} recycledPlasticPercentage={recycledPlasticPercentage} />
                </AccordionDetails>
              </Accordion>
            </>
          )}
          <Grid container sx={{ gap: 5, pt: 1 }}>
            <Grid item>
              <Tooltip title={t('common.actions.edit')} placement="top">
                <IconButton onClick={() => setIsEditing(true)}>
                  <EditIcon sx={{ fontSize: 25 }} />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid item>
              <Tooltip title={t('organizations.product.lots.addLots.dialog.title')} placement="top">
                <IconButton onClick={() => setIsAdding(true)}>
                  <AddIcon sx={{ fontSize: 25 }} />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid item>
              <Tooltip title={t('common.actions.delete')} placement="top">
                <IconButton onClick={() => setIsDeleting(true)}>
                  <DeleteIcon sx={{ fontSize: 25 }} />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
          <CardActions>
            {childrensLot && childrensLot.length > 0 && (
              <Typography>
                <IconButton onClick={() => setExpanded(!expanded)} aria-expanded={expanded} aria-label={t('aria.icons.showMore')}>
                  {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                {t('products.lots.subLots')}
              </Typography>
            )}
          </CardActions>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <CardContent sx={{ pt: 0 }}>
              {pagination}
              {childrensLot &&
                childrensLot.map((childrenLot) => {
                  return (
                    <TreeLotsCard
                      key={childrenLot.id}
                      ean={ean}
                      opk={opk}
                      id={childrenLot.id}
                      authority={childrenLot.authority}
                      code={childrenLot.code}
                      info={childrenLot.info}
                      from={childrenLot.valid_from}
                      to={childrenLot.valid_to}
                      childrens={childrenLot.children}
                    />
                  );
                })}
            </CardContent>
          </Collapse>
          {expanded && (
            <Button variant="contained" onClick={handleScrollToExpandMore}>
              {t('organizations.product.lots.returnToFather')}
            </Button>
          )}
        </CardContent>
      </Card>
      <AddLotDialog action="add" ean={ean} opk={opk} id={id} isOpen={isAdding} setIsOpen={setIsAdding} />
      <AddLotDialog action="edit" ean={ean} opk={opk} id={id} isOpen={isEditing} setIsOpen={setIsEditing} authority={authority} code={code} from={from} to={to} />
      <DeleteLotDialog isOpen={isDeleting} setIsOpen={setIsDeleting} ean={ean} opk={opk} id={id} />
    </Box>
  );
}

export default TreeLotsCard;

const materialShape = PropTypes.shape({});

const infoShape = PropTypes.shape({
  type: PropTypes.string,
  surfaceWeight: PropTypes.number,
  pieceSurface: PropTypes.number,
  plasticPercentage: PropTypes.number,
  recycledPlasticPercentage: PropTypes.number,
  materials: PropTypes.arrayOf(materialShape),
});

TreeLotsCard.propTypes = {
  ean: PropTypes.string.isRequired,
  opk: PropTypes.string.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  authority: PropTypes.string.isRequired,
  code: PropTypes.string.isRequired,
  info: infoShape,
  from: PropTypes.string,
  to: PropTypes.string,
  searchParamsName: PropTypes.string,
};
